import React, { useState, useEffect } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, Lock, Terminal, Target, AlertTriangle, TrendingUp, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import BlogPostCard from '@/components/BlogPostCard';
import { blogPosts } from '@/data/blogPosts';
import { motion } from 'framer-motion';

const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get the newest blog post for the featured post section
  const featuredPost = blogPosts.find(post => post.id === '4') || blogPosts[0];
  // Get the rest of the blog posts for the grid section
  const otherPosts = blogPosts.filter(post => post.id !== featuredPost?.id);
  
  // Cybersecurity-focused placeholder posts if needed
  const placeholderPosts = [
    {
      id: 'placeholder-1',
      title: 'Zero Trust Architecture: A Comprehensive Implementation Guide',
      excerpt: 'Learn how to implement zero trust security principles across your organization with practical strategies and real-world examples.',
      imageUrl: '/images/blog/zero-trust.jpg',
      date: 'Coming Soon',
      slug: '#',
      category: 'Cloud Security'
    },
    {
      id: 'placeholder-2',
      title: 'DevSecOps Best Practices: Integrating Security in CI/CD Pipelines',
      excerpt: 'Discover how to seamlessly integrate security testing and compliance checks into your development workflow without slowing down delivery.',
      imageUrl: '/images/blog/devsecops.jpg',
      date: 'Coming Soon',
      slug: '#',
      category: 'DevSecOps'
    },
    {
      id: 'placeholder-3',
      title: 'Advanced Offensive Security Techniques for Modern Applications',
      excerpt: 'Explore cutting-edge offensive security methodologies and tools for identifying vulnerabilities in cloud-native applications.',
      imageUrl: '/images/blog/pentest.jpg',
      date: 'Coming Soon',
      slug: '#',
      category: 'Offensive Security'
    },
    {
      id: 'placeholder-4',
      title: 'SAST vs DAST: Choosing the Right Application Security Testing Strategy',
      excerpt: 'Compare static and dynamic application security testing approaches to build a comprehensive security testing strategy.',
      imageUrl: '/images/blog/appsec.jpg',
      date: 'Coming Soon',
      slug: '#',
      category: 'Application Security'
    }
  ];
  
  const displayPosts = otherPosts.length > 0 ? otherPosts : placeholderPosts;

  
  return (
    <PageLayout>
      <SEO
        title="Cybersecurity Blog | Expert Penetration Testing & DevSecOps Security Insights"
        description="Latest cybersecurity insights, penetration testing tutorials, DevSecOps automation guides, threat intelligence analysis, and application security best practices from CIPHER Security's expert team. Stay ahead of cyber threats with actionable security guidance, vulnerability management strategies, and compliance frameworks."
        imageUrl={featuredPost?.imageUrl || "/images/blog/security-insights.jpg"}
        keywords={[
          'cybersecurity blog 2025',
          'penetration testing tutorials',
          'DevSecOps automation guides',
          'cybersecurity expert insights',
          'threat intelligence blog',
          'application security tutorials',
          'cloud security best practices',
          'incident response playbooks',
          'vulnerability management guides',
          'zero trust architecture blog',
          'security compliance frameworks',
          'ethical hacking tutorials',
          'cyber threat hunting guides',
          'security awareness training blog',
          'SAST/DAST security testing'
        ]}
        type="website"
      />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 30%, rgba(0, 168, 107, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 70%, rgba(6, 182, 212, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 50%, rgba(251, 191, 36, 0.1) 0%, transparent 70%)" }} />
        </div>

        <div className="container mx-auto px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(0, 168, 107, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#00a86b",
                    fontWeight: 700,
                  }}
                >
                  Cybersecurity Insights
                </span>
                <span className="block mt-2" style={{ fontSize: "0.7em", color: "#e5e7eb" }}>
                  Expert Analysis & Industry Intelligence
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto"
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Stay ahead of evolving threats with in-depth analysis, best practices, and actionable security guidance from our expert team.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section
        className="py-16 px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            {/* Featured Article Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                style={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Featured Security Analysis
              </h2>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                Deep insights from our cybersecurity experts
              </p>
            </motion.div>

            {/* Featured Post */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="mb-16"
            >
              {featuredPost && (
                <Card
                  className="group cursor-pointer relative overflow-hidden"
                  style={{
                    background: "rgba(6, 182, 212, 0.08)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(6, 182, 212, 0.30)",
                    borderRadius: 16,
                    padding: 0,
                    transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformStyle: "preserve-3d",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.15)";
                    e.currentTarget.style.borderColor = "#06b6d4";
                    e.currentTarget.style.boxShadow = "0 20px 40px rgba(6, 182, 212, 0.20)";
                    e.currentTarget.style.transform = "translateY(-8px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(6, 182, 212, 0.08)";
                    e.currentTarget.style.borderColor = "rgba(6, 182, 212, 0.30)";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0px)";
                  }}
                >
                  <CardContent style={{ padding: 32 }}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      <div className="lg:col-span-2 space-y-6">
                        <div className="flex items-start mb-6">
                          <div
                            className="flex items-center justify-center mr-4"
                            style={{
                              width: 64,
                              height: 64,
                              padding: 16,
                              borderRadius: 12,
                              backgroundColor: "#06b6d420",
                              color: "#06b6d4",
                              marginBottom: 24,
                              boxShadow: "0 4px 16px #06b6d420",
                              transition: "transform 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                              e.currentTarget.style.filter = "drop-shadow(0 0 20px #06b6d4)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.filter = "none";
                            }}
                          >
                            <Shield className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <h3
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: 700,
                                color: "#fff",
                                marginBottom: 8,
                              }}
                            >
                              {featuredPost.title}
                            </h3>
                            <div
                              className="flex items-center gap-4 text-sm mb-4"
                              style={{ color: "#9ca3af" }}
                            >
                              <span className="flex items-center">
                                <Lock className="w-4 h-4 mr-1" style={{ color: "#06b6d4" }} />
                                {featuredPost.category || "Security Insights"}
                              </span>
                              <span>Published: {featuredPost.date}</span>
                            </div>
                          </div>
                        </div>
                        <p
                          style={{
                            color: "#d1d5db",
                            fontSize: 16,
                            lineHeight: 1.6,
                            marginBottom: 16,
                          }}
                        >
                          {featuredPost.excerpt}
                        </p>
                        <Link
                          to={`/blog/${featuredPost.slug}`}
                          className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                          style={{
                            color: "#06b6d4",
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          Read Full Article
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Link>
                      </div>
                      <div
                        style={{
                          background: "linear-gradient(135deg, #06b6d4 0%, #00a86b 100%)",
                          borderRadius: 16,
                          padding: 24,
                          color: "#fff",
                          minWidth: 200,
                          boxShadow: "0 4px 24px 0 rgba(6, 182, 212, 0.15)",
                        }}
                      >
                        <h4
                          style={{
                            fontWeight: 700,
                            fontSize: 20,
                            color: "#fff",
                            marginBottom: 16,
                            letterSpacing: "0.04em",
                          }}
                        >
                          Featured Article
                        </h4>
                        <div className="grid grid-cols-1 gap-3">
                          {[
                            "Expert Analysis",
                            "Real-World Examples", 
                            "Actionable Insights",
                            "Best Practices",
                            "Industry Trends"
                          ].map((feature, idx) => (
                            <div
                              key={idx}
                              className="flex items-center"
                              style={{
                                color: "#e5e7eb",
                                fontSize: 14,
                              }}
                            >
                              <div
                                style={{
                                  width: 6,
                                  height: 6,
                                  background: "#fff",
                                  borderRadius: "50%",
                                  marginRight: 8,
                                }}
                              />
                              {feature}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>

            {/* Categories Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-16"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <h2
                  style={{
                    fontSize: "2.25rem",
                    fontWeight: 700,
                    color: "#fff",
                    letterSpacing: "-0.01em",
                    marginBottom: 16,
                  }}
                >
                  Explore Security Topics
                </h2>
                <p
                  style={{
                    fontSize: "1.125rem",
                    color: "#d1d5db",
                    maxWidth: 800,
                    margin: "0 auto",
                  }}
                >
                  Deep dive into specific areas of cybersecurity
                </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {[
                  {
                    title: "Application Security",
                    icon: <Shield className="w-6 h-6" />,
                    description: "SAST, DAST, SCA, and secure development practices",
                    count: "12 articles",
                    color: "#00a86b",
                    bgColor: "rgba(0, 168, 107, 0.08)",
                    borderColor: "rgba(0, 168, 107, 0.30)",
                    hoverBg: "rgba(0, 168, 107, 0.15)",
                    shadowColor: "rgba(0, 168, 107, 0.20)"
                  },
                  {
                    title: "DevSecOps",
                    icon: <Terminal className="w-6 h-6" />,
                    description: "CI/CD security integration and automation",
                    count: "8 articles",
                    color: "#06b6d4",
                    bgColor: "rgba(6, 182, 212, 0.08)",
                    borderColor: "rgba(6, 182, 212, 0.30)",
                    hoverBg: "rgba(6, 182, 212, 0.15)",
                    shadowColor: "rgba(6, 182, 212, 0.20)"
                  },
                  {
                    title: "Offensive Security",
                    icon: <Target className="w-6 h-6" />,
                    description: "Advanced testing methodologies and techniques",
                    count: "10 articles",
                    color: "#fbbf24",
                    bgColor: "rgba(251, 191, 36, 0.08)",
                    borderColor: "rgba(251, 191, 36, 0.30)",
                    hoverBg: "rgba(251, 191, 36, 0.15)",
                    shadowColor: "rgba(251, 191, 36, 0.20)"
                  },
                  {
                    title: "Threat Intelligence",
                    icon: <AlertTriangle className="w-6 h-6" />,
                    description: "Latest threats and vulnerability analysis",
                    count: "15 articles",
                    color: "#a855f7",
                    bgColor: "rgba(168, 85, 247, 0.08)",
                    borderColor: "rgba(168, 85, 247, 0.30)",
                    hoverBg: "rgba(168, 85, 247, 0.15)",
                    shadowColor: "rgba(168, 85, 247, 0.20)"
                  }
                ].map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.8, 
                      delay: index * 0.1,
                      ease: [0.16, 1, 0.3, 1]
                    }}
                  >
                    <Card
                      className="h-full group cursor-pointer relative overflow-hidden"
                      style={{
                        background: category.bgColor,
                        backdropFilter: "blur(8px)",
                        border: `1px solid ${category.borderColor}`,
                        borderRadius: 16,
                        padding: 0,
                        transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                        transformStyle: "preserve-3d",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = category.hoverBg;
                        e.currentTarget.style.borderColor = category.color;
                        e.currentTarget.style.boxShadow = `0 20px 40px ${category.shadowColor}`;
                        e.currentTarget.style.transform = "translateY(-8px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = category.bgColor;
                        e.currentTarget.style.borderColor = category.borderColor;
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.transform = "translateY(0px)";
                      }}
                    >
                      <CardContent style={{ padding: 32 }}>
                        <div className="flex items-start mb-6">
                          <div
                            className="flex items-center justify-center mr-4"
                            style={{
                              width: 64,
                              height: 64,
                              padding: 16,
                              borderRadius: 12,
                              backgroundColor: `${category.color}20`,
                              color: category.color,
                              marginBottom: 24,
                              boxShadow: `0 4px 16px ${category.color}20`,
                              transition: "transform 0.3s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.1)";
                              e.currentTarget.style.filter = `drop-shadow(0 0 20px ${category.color})`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.filter = "none";
                            }}
                          >
                            {category.icon}
                          </div>
                          <div className="flex-1">
                            <h3
                              style={{
                                fontSize: "1.5rem",
                                fontWeight: 700,
                                color: "#fff",
                                marginBottom: 8,
                              }}
                            >
                              {category.title}
                            </h3>
                            <p
                              style={{
                                color: "#d1d5db",
                                fontSize: 16,
                                lineHeight: 1.6,
                                marginBottom: 16,
                              }}
                            >
                              {category.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span
                            style={{
                              color: "#9ca3af",
                              fontSize: 14,
                              fontWeight: 600,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}
                          >
                            {category.count}
                          </span>
                          <button
                            className="inline-flex items-center font-medium transition-all group-hover:translate-x-1"
                            style={{
                              color: category.color,
                              fontSize: 16,
                              fontWeight: 600,
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                            }}
                          >
                            Explore
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </button>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Newsletter Signup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.8, 
                delay: 0.4,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <Card
                className="text-center group relative overflow-hidden"
                style={{
                  background: "rgba(59, 130, 246, 0.08)",
                  backdropFilter: "blur(8px)",
                  border: "1px solid rgba(59, 130, 246, 0.30)",
                  borderRadius: 16,
                  padding: 0,
                  transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
                  transformStyle: "preserve-3d",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(59, 130, 246, 0.15)";
                  e.currentTarget.style.borderColor = "#3b82f6";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(59, 130, 246, 0.20)";
                  e.currentTarget.style.transform = "translateY(-8px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(59, 130, 246, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(59, 130, 246, 0.30)";
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "translateY(0px)";
                }}
              >
                <CardContent style={{ padding: 32 }}>
                  <div
                    className="flex items-center justify-center mx-auto mb-6"
                    style={{
                      width: 64,
                      height: 64,
                      padding: 16,
                      borderRadius: 12,
                      backgroundColor: "#3b82f620",
                      color: "#3b82f6",
                      boxShadow: "0 4px 16px #3b82f620",
                      transition: "transform 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "scale(1.1)";
                      e.currentTarget.style.filter = "drop-shadow(0 0 20px #3b82f6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.filter = "none";
                    }}
                  >
                    <Shield className="w-6 h-6" />
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#fff",
                      marginBottom: 8,
                    }}
                  >
                    Stay Security-Informed
                  </h3>
                  <p
                    style={{
                      color: "#d1d5db",
                      fontSize: 16,
                      lineHeight: 1.6,
                      marginBottom: 16,
                    }}
                  >
                    Get the latest cybersecurity insights, threat intelligence, and best practices delivered to your inbox weekly.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto mt-6">
                    <input
                      type="email"
                      placeholder="Enter your email"
                      className="flex-1 px-4 py-3"
                      style={{
                        borderRadius: "8px",
                        background: "rgba(255, 255, 255, 0.05)",
                        border: "1px solid rgba(255,255,255,0.18)",
                        color: "#ffffff",
                        outline: "none",
                        fontSize: "1rem",
                        minWidth: 0,
                      }}
                    />
                    <Button
                      className="px-6 py-3 transition-all hover:shadow-xl"
                      style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
                        color: "#fff",
                        borderRadius: "8px",
                        fontWeight: 600,
                        border: "none",
                        boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.25)",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #2563eb 0%, #0891b2 100%)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      Subscribe
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Blog;